import React from 'react';

import { // eslint-disable-line import/no-extraneous-dependencies
  ChartSquareBarIcon,
  MapIcon,
  FilterIcon,
  ChartPieIcon,
  PresentationChartLineIcon,
  TrendingUpIcon,
} from '@heroicons/react/outline';
import { rockerboxPerformanceIcon, IndexGridNew } from '@rockerbox/styleguide';
import moment from 'moment';
import { toast } from 'react-hot-toast';
import { Header, Table, Icon, Button } from 'semantic-ui-react';
import styled from 'styled-components';

import { track } from '../../../utils/tracking';
import { BasicViewHeader } from '../../components';
import { buildHash, buildPath } from '../../hooks/urlState';
import * as routes from '../../routes';
import Link from '../../utils/link';

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 15px;
  padding-bottom: 15px;
  width: 100%;
`;

export const routeDisplayMapping = {
  crossChannelReport: {
    display: 'Cross Channel Report',
    path: routes.crossChannelReport,
    icon: <ChartSquareBarIcon style={{ height: 25, margin: 0 }} />,
  },
  conversionFunnel: {
    display: 'Conversions Funnel',
    path: routes.conversionFunnel,
    icon: <FilterIcon style={{ transform: 'rotate(-90deg)', height: 25, margin: 0 }} />,
  },
  funnelPosition: {
    display: 'Funnel Position',
    path: routes.funnelPosition,
    icon: <FilterIcon style={{ height: 25, margin: 0 }} />,
  },
  marketingPaths: {
    display: 'Marketing Paths',
    path: routes.marketingPaths,
    icon: <MapIcon style={{ height: 25, margin: 0 }} />,
  },
  channelOverlap: {
    display: 'Channel Overlap',
    path: routes.channelOverlap,
    icon: <ChartPieIcon style={{ height: 25, margin: 0 }} />,
  },
  spendTrends: {
    display: 'Spend Trends',
    path: routes.spendTrends,
    icon: <PresentationChartLineIcon style={{ height: 25, margin: 0 }} />,
  },
  platformPerformance: {
    display: 'Platform Performance',
    path: routes.platformPerformance,
    icon: <PresentationChartLineIcon style={{ height: 25, margin: 0 }} />,
  },
  rockerboxPerformance: {
    display: 'Rockerbox Performance',
    path: routes.rockerboxPerformance,
    icon: <img src={rockerboxPerformanceIcon} alt="rockerbox navigation icon" style={{ height: 25, margin: 0 }} />,
  },
  platformBenchmarks: {
    display: 'Platform Benchmarks',
    path: routes.platformBenchmarks,
    icon: <TrendingUpIcon style={{ height: 25, margin: 0 }} />,
  },
};

export const MainHeader = ({ topRight }) => (
  <BasicViewHeader
    header="Saved Reports"
    subheader=""
    topRight={topRight}
  />
);

export const SavedReportsHeader = ({ topRight }) => (
  <Wrapper className="generic-header">
    <div>
      <Header as="h1" style={{ fontWeight: 'bold', color: '#333' }}>
        <div
          className="header"
          style={{
            fontWeight: 600,
            padding: '5px 0',
            display: 'flex',
            alignItems: 'center',
            columnGap: 20,
          }}
        >
          <h1>Saved Reports</h1>
        </div>
      </Header>
    </div>
    {topRight && (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {topRight}
      </div>
    )}
  </Wrapper>
);

export const StatusCell = ({ item }) => {
  const statusMapping = {
    SUCCESS: {
      color: 'green',
      icon: 'checkmark box',
      display: 'Ready',
    },
    ERROR: {
      color: 'red',
      icon: 'exclamation triangle box',
      display: 'Error',
    },
    EXPIRED: {
      color: 'red',
      icon: 'calendar times',
      display: 'Expired',
    },
    IN_PROGRESS: {
      color: 'teal',
      icon: 'refresh times',
      display: 'In Progress',
    },
    EMPTY: {
      color: 'gray',
      icon: 'dont',
      display: 'No Data',
    },
  };

  const statusColor = statusMapping[item.delivery_status.toUpperCase()]?.color;
  const statusIcon = statusMapping[item.delivery_status.toUpperCase()]?.icon;
  const statusDisplay = statusMapping[item.delivery_status.toUpperCase()]?.display;

  return (
    <Table.Cell>
      <Icon style={{ marginLeft: 8 }} name={statusIcon} color={statusColor} size="large" />
      {' '}
      {statusDisplay}
    </Table.Cell>
  );
};

export const TitleCell = ({ item }) => {
  const { name, route, params, id, user, shared } = item;
  const rebuiltPath = buildPath(route, params.route);
  const rebuiltHash = params.hash ? buildHash(params.hash) : '';
  const fullPath = rebuiltPath + rebuiltHash;

  return (
    <Table.Cell>
      <Link
        to={fullPath}
        onClick={() => {
          track('saved_report.open', { id, name, user, shared, route, params });
        }}
      >
        {name}
      </Link>
    </Table.Cell>
  );
};

export const BasedOnCell = ({ item }) => {
  const { route } = item;
  return (
    <Table.Cell>
      <Link to={routeDisplayMapping[route].path}>
        {routeDisplayMapping[route].display}
      </Link>
    </Table.Cell>
  );
};

export const TimeCell = ({ item }) => {
  const { created_at } = item;
  const dateString = moment(created_at).format('DD-MMM-YYYY');
  return (
    <Table.Cell>
      <p>{dateString}</p>
    </Table.Cell>
  );
};

export const CustomerTypeCell = ({ item }) => (
  <Table.Cell>
    {item.customer_type === 'new'
      ? 'New'
      : item.customer_type === 'repeat'
        ? 'Repeat'
        : 'All'}
  </Table.Cell>
);

export const AttributionModelCell = ({ item }) => (
  <Table.Cell>
    {item.model_type === 'first'
      ? 'First Touch'
      : item.model_type === 'last'
        ? 'Last Touch'
        : item.model_type === 'even'
          ? 'Even'
          : 'Normalized'}
  </Table.Cell>
);

export const CadenceCell = ({ item }) => {
  const formatCadence = cadenceValue => {
    const cadenceMap = {
      day: 'Daily',
      week: 'Weekly',
      month: 'Monthly',
    };
    return cadenceMap[cadenceValue] || cadenceValue;
  };

  return (
    <Table.Cell>
      {formatCadence(item.cadence)}
    </Table.Cell>
  );
};

export const LastDeliveredCell = ({ item }) => (
  <Table.Cell>
    {item.delivery_timestamp ? moment(item.delivery_timestamp).format('MMM D, YYYY hh:mm:ss') : 'Never'}
  </Table.Cell>
);

export const ConversionCell = segments => ({ item }) => {
  const segment = segments?.find((({ identifier }) => identifier === item?.filter_id))?.action_name;

  return (
    <Table.Cell>
      <p>{segment}</p>
    </Table.Cell>
  );
};

export const DeliveryWindowCell = ({ item }) => {
  const { start_date, end_date } = item;
  if (!start_date || !end_date) return <Table.Cell>-</Table.Cell>;

  return (
    <Table.Cell>
      {`${moment(start_date).format('YYYY-MM-DD')} - ${moment(end_date).format('YYYY-MM-DD')}`}
    </Table.Cell>
  );
};

export const ReportManageRow = ({ item, col, showDelete = true }) => {
  const { id, name, user, shared, route, params, download_url } = item;
  const { deleteSavedReport, handleGetSavedReports, openDetailsDrawer, onDetailsButtonClick } = col.key;

  const handleDelete = () => {
    deleteSavedReport(id)
      .then(() => {
        toast.success('Deleted Saved Report');
        track('saved_report.delete', { id, name, user, shared, route, params });
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.log('Delete saved report error:', err);
        toast.error('Error deleting saved report');
      })
      .finally(() => handleGetSavedReports());
  };

  const isExpired = item.download_url === 'EXPIRED' || new Date(item.delivery_timestamp) < moment().subtract(2, 'days').toDate();

  return (
    <Table.Cell textAlign="right">
      <div style={{ display: 'inline-flex', gap: '10px', alignItems: 'center' }}>
        {download_url && (
          <Button icon inverted size="large" style={{ padding: 0, margin: 0 }} disabled={isExpired}>
            <a href={download_url} download target="_blank" rel="noreferrer">
              <Icon name="download" />
            </a>
          </Button>
        )}
        <Button
          icon
          inverted
          size="large"
          style={{ padding: 0, margin: 0 }}
          onClick={() => {
            onDetailsButtonClick(item);
            openDetailsDrawer();
          }}
        >
          <Icon name="search" color="purple" />
        </Button>
        {showDelete && <IndexGridNew.OverlayDeleteBtn onClick={handleDelete} />}
      </div>
    </Table.Cell>
  );
};

export const EmailRecipientsCell = ({ item }) => {
  const { email_recipients } = item;
  if (!email_recipients || !email_recipients.length) {
    return (
      <Table.Cell>-</Table.Cell>
    );
  }

  return (
    <Table.Cell>
      <p style={{
        margin: 0,
        wordBreak: 'break-word',
      }}
      >
        {email_recipients.join(', ')}
      </p>
    </Table.Cell>
  );
};

export const ReportTypeCell = ({ item }) => (
  <Table.Cell>
    {item.report_config_id === 0 ? 'Adhoc' : 'Scheduled'}
  </Table.Cell>
);
